import { PERMISSIONS } from '@ibe/constants';
import { Button, ConfirmDialog, StatusBadge, toneForStatus } from '@ibe/ui';
import { Eye, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { PermissionGuard } from '../../../auth/permission-guard.js';
import { PrimaryCell, RowActions } from '../../../components/resource-list/cells.js';
import { ResourceListPage } from '../../../components/resource-list/resource-list-page.js';
import { type ColumnDescriptor } from '../../../lib/resource-list/columns.js';
import { type FilterDescriptor } from '../../../lib/resource-list/filters.js';
import { paymentPolicyEndpoints } from '../api/endpoints.js';
import { useSoftDeletePaymentPolicy } from '../api/mutations.js';
import { POLICY_STATUS_OPTIONS, type PaymentPolicyRow, paymentPolicyRow } from '../api/schemas.js';

/**
 * The payment policies list.
 *
 * Search, sort, pagination, filtering and the three non-data states all come
 * from `ResourceListPage`. What is here is the descriptor: which columns
 * exist, which backend fields each one reads, and what a row can be done to.
 *
 * `contentHtml` is not a column and must not become one. A table cell cannot
 * show a document, and projecting it would ship up to 64 kB per row to render
 * nothing.
 */

const filters: readonly FilterDescriptor[] = [
  {
    id: 'propertyId',
    label: 'Property',
    param: 'propertyId',
    kind: 'entity',
    optionsPath: '/v1/properties/options',
  },
  {
    id: 'status',
    label: 'Status',
    param: 'status',
    kind: 'select',
    options: POLICY_STATUS_OPTIONS,
  },
];

/** A UTC calendar date. The value is an ISO instant; the day is what reads. */
function formatDate(value: string | undefined): string {
  if (value === undefined) return '';
  return new Date(value).toLocaleDateString('en-AU', {
    timeZone: 'UTC',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export const paymentPolicyColumns: readonly ColumnDescriptor<PaymentPolicyRow>[] = [
  {
    id: 'name',
    header: 'Policy',
    sortable: true,
    alwaysVisible: true,
    fields: ['name', 'code'],
    cell: (row) => (
      <PrimaryCell
        label={row.name ?? row.id}
        to={`/payment-policies/${row.id}`}
        secondary={row.code ?? null}
      />
    ),
  },
  {
    id: 'status',
    header: 'Status',
    sortable: true,
    fields: ['status'],
    cell: (row) =>
      row.status === undefined ? null : (
        <StatusBadge tone={toneForStatus(row.status)}>{row.status}</StatusBadge>
      ),
  },
  {
    id: 'updatedAt',
    header: 'Updated',
    sortable: true,
    fields: ['updatedAt'],
    cell: (row) => formatDate(row.updatedAt),
  },
  {
    id: 'actions',
    header: '',
    headerLabel: 'Actions',
    align: 'right',
    alwaysVisible: true,
    fields: ['name'],
    cell: (row: PaymentPolicyRow): React.JSX.Element => <PaymentPolicyRowActions row={row} />,
  },
];

/**
 * The per-row controls, as a component rather than inline markup.
 *
 * Deleting needs a mutation, a confirmation and a permission check, and a
 * column descriptor's `cell` is a plain function — it cannot hold hooks. This
 * is the smallest thing that can.
 */
function PaymentPolicyRowActions({ row }: { readonly row: PaymentPolicyRow }): React.JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const softDelete = useSoftDeletePaymentPolicy();
  const label = row.name ?? row.id;

  return (
    <>
      <RowActions
        actions={[
          { label: `View ${label}`, icon: Eye, to: `/payment-policies/${row.id}` },
          { label: `Edit ${label}`, icon: Pencil, to: `/payment-policies/${row.id}/edit` },
        ]}
      />
      <PermissionGuard permission={PERMISSIONS.PAYMENT_POLICY_DELETE}>
        <RowActions
          actions={[
            {
              label: `Delete ${label}`,
              icon: Trash2,
              destructive: true,
              onClick: () => {
                setConfirming(true);
              },
            },
          ]}
        />
      </PermissionGuard>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Delete payment policy"
        description={`${label} will be removed from this list. Rate plans that reference it keep selling, with no payment terms attached — check them before you delete. Existing reservations and their payments are unaffected.`}
        confirmLabel="Delete"
        destructive
        loading={softDelete.isPending}
        onConfirm={() => {
          softDelete.mutate(row.id, {
            onSuccess: () => {
              setConfirming(false);
            },
          });
        }}
      />
    </>
  );
}

export function PaymentPolicyListPage(): React.JSX.Element {
  const { search } = useLocation();

  return (
    <ResourceListPage
      title="Payment policies"
      description="When a guest is charged, what deposit is taken, and what a rate plan applies."
      path={paymentPolicyEndpoints.list}
      resource="paymentPolicies"
      schema={paymentPolicyRow}
      columns={paymentPolicyColumns}
      filters={filters}
      rowKey={(row) => row.id}
      permission={PERMISSIONS.PAYMENT_POLICY_READ}
      actions={
        <PermissionGuard permission={PERMISSIONS.PAYMENT_POLICY_CREATE}>
          <Button asChild>
            <Link to={{ pathname: '/payment-policies/new', search }}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              New payment policy
            </Link>
          </Button>
        </PermissionGuard>
      }
    />
  );
}
